import { ActionIcon, TextInput, type TextInputProps } from '@any-ui-react/core';
import { type RefObject, useEffect, useState } from 'react';
import { MaterialIcons } from '../material-icons';

export interface InputSearchProps extends Omit<TextInputProps, 'value'> {
  hideLeftSearchIcon?: boolean;
  initialValue?: string | null;
  innerRef?: RefObject<HTMLInputElement>;
  searchOnType?: boolean;
  value?: string | null;
  onClear?: () => void;
  onSearch?: (text: string) => void;
}

export const InputSearch = ({
  hideLeftSearchIcon = false,
  initialValue,
  innerRef,
  searchOnType = false,
  value,
  onClear,
  onSearch,
  ...rest
}: InputSearchProps) => {
  const [searchValue, setSearchValue] = useState<string>(initialValue || '');

  useEffect(() => {
    setSearchValue(value || '');
  }, [value]);

  useEffect(() => {
    const search = (e: Event) => {
      const sValue = (e.target as HTMLInputElement)?.value;
      setSearchValue(sValue);
      onSearch?.(sValue);
    };

    if (innerRef?.current) {
      const input = innerRef.current;
      input.addEventListener('search', search, false);
      return () => {
        input.removeEventListener('search', search, false);
      };
    }

    return () => null;
  }, [onSearch, innerRef]);

  const clear = () => {
    setSearchValue('');
    onClear?.();
  };

  return (
    <TextInput
      {...rest}
      ref={innerRef}
      leftSection={hideLeftSearchIcon ? undefined : <MaterialIcons iconName="search" className="text-gray-7" />}
      rightSection={
        searchValue && (
          <ActionIcon onClick={clear}>
            <MaterialIcons iconName="cancel" fill={true} className="text-gray-5 cursor-pointer" />
          </ActionIcon>
        )
      }
      rightSectionProps={{ className: '' }}
      type="search"
      value={searchValue}
      onChange={e => {
        setSearchValue(e.target.value);
        if (searchOnType) {
          onSearch?.(e.target.value);
        }
      }}
    />
  );
};
